import {
  isLoanStatus,
  type LoanStatus,
} from '../../enums/loan-status.enum.js'
import { isOneOf } from '../../helpers/is-one-of.js'

/**
 * Display stages for loan issues. Happy-path statuses collapse here so a
 * rule cannot skip a stage by omitting a key.
 */
export const LOAN_ISSUE_PHASES = [
  'PRE_APPROVAL',
  'ESTIMATE',
  'CLOSING',
] as const
export type LoanIssuePhase = (typeof LOAN_ISSUE_PHASES)[number]

export const LOAN_ISSUE_PHASE_LABELS: Record<LoanIssuePhase, string> = {
  PRE_APPROVAL: 'Pre-approval',
  ESTIMATE: 'Loan Estimate',
  CLOSING: 'Closing',
}

/** Happy-path statuses a catalog rule must allow or block. */
export const LOAN_ISSUE_BLOCK_STATUSES = [
  'PRE_APPROVED',
  'APPROVED',
  'CLEAR_TO_CLOSE',
  'CLEAR_TO_CLOSE_FUND',
  'ORIGINATED',
] as const
export type LoanIssueBlockStatus = (typeof LOAN_ISSUE_BLOCK_STATUSES)[number]

const PRE_APPROVAL_STATUSES = ['INCOMPLETE', 'UNDERWRITING'] as const
const ESTIMATE_STATUSES = ['PRE_APPROVED', 'APPROVED'] as const
const CLOSING_STATUSES = [
  'CLEAR_TO_CLOSE',
  'CLEAR_TO_CLOSE_FUND',
  'ORIGINATED',
] as const

/**
 * Phase for issue chrome. `null` on exception / terminal statuses
 * (no nav marks, no catalog display).
 */
export function loanIssuePhase(status: LoanStatus): LoanIssuePhase | null {
  if (isOneOf(status, PRE_APPROVAL_STATUSES)) return 'PRE_APPROVAL'
  if (isOneOf(status, ESTIMATE_STATUSES)) return 'ESTIMATE'
  if (isOneOf(status, CLOSING_STATUSES)) return 'CLOSING'
  return null
}

export function isLoanIssueBlockStatus(
  value: string,
): value is LoanIssueBlockStatus {
  return isOneOf(value, LOAN_ISSUE_BLOCK_STATUSES)
}

export function isLoanIssuePhase(value: string): value is LoanIssuePhase {
  return isOneOf(value, LOAN_ISSUE_PHASES)
}

export function parseLoanIssuePhase(
  status: string,
): LoanIssuePhase | null {
  if (!isLoanStatus(status)) return null
  return loanIssuePhase(status)
}
